import React, { useEffect, useState, useCallback } from "react";
import Highcharts from "highcharts";
import rawData from "./data.json";
import { FiFilter, FiRefreshCw, FiInfo } from "react-icons/fi";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Alert, OverlayTrigger, Tooltip } from "react-bootstrap";

const ScatterJitterPlot = () => {
  // Unified range controller
  const [range, setRange] = useState([0, 2000]);
  const [inputRange, setInputRange] = useState([0, 2000]);
  const [tempInputRange, setTempInputRange] = useState([0, 2000]);
  const [isApplying, setIsApplying] = useState(false);

  const MAX_DATA_VALUE = (() => {
    let max = 0;
    Object.values(rawData).forEach((items) => {
      items.forEach((d) => {
        if (d.medicare_percentage > max) max = d.medicare_percentage;
      });
    });
    return max;
  })();

  /** -------------------------------
   * Extract medicare percentages only
   * -------------------------------- */
  const extractMedicareValues = (data) => {
    const result = {};
    Object.keys(data).forEach((code) => {
      result[code] = data[code].map((item) => item.medicare_percentage);
    });
    return result;
  };

  const medicareOnly = extractMedicareValues(rawData);

  /** --------------------------------
   * Filter using new range values
   * -------------------------------- */
  const filterData = (data, [min, max]) => {
    const filtered = {};
    Object.keys(data).forEach((key) => {
      filtered[key] = data[key].filter((value) => value >= min && value <= max);
    });
    return filtered;
  };

  /** --------------------------------
   * Y-axis config (dynamic max)
   * -------------------------------- */
  const getYAxisConfig = (filteredData) => {
    const dynamicMax = Math.max(...Object.values(filteredData).flat(), 100);

    return {
      type: "linear",
      min: 0,
      max: dynamicMax,
      title: {
        text: "Medicare Percentage",
        style: {
          color: "#64748B",
          fontSize: "14px",
          fontWeight: "600",
          fontFamily: "Roboto Flex",
        },
      },
      labels: {
        style: {
          color: "#64748B",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      gridLineColor: "#F8FAFC",
      gridLineWidth: 1,
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
    };
  };

  const variationConfig = {
    colors: ["#93C5FD", "#6EE7B7", "#FCD34D", "#C4B5FD", "#FCA5A5", "#67E8F9"],
    markerType: "circle",
    markerRadius: 5,
    jitter: { x: 0.35, y: 0 },
  };

  /** -------------------------------
   * DRAW CHART
   * -------------------------------- */
  useEffect(() => {
    const filteredMedicare = filterData(medicareOnly, range);
    const categories = Object.keys(filteredMedicare);
    const yAxisConfig = getYAxisConfig(filteredMedicare);

    const series = categories.map((cat, index) => {
      const points = rawData[cat]
        .filter((item) =>
          filteredMedicare[cat].includes(item.medicare_percentage)
        )
        .map((item) => ({
          x: index,
          y: item.medicare_percentage,
          negotiated: item.negotiated_rates,
          provider: item.provider_name,
        }));

      const color =
        variationConfig.colors[index % variationConfig.colors.length];

      return {
        name: cat,
        data: points,
        color: color,
        marker: {
          symbol: variationConfig.markerType,
          radius: variationConfig.markerRadius,
          fillColor: color,
          lineColor: "rgba(255, 255, 255, 0.8)",
          lineWidth: 1,
          states: {
            hover: { enabled: true, lineColor: "#FFFFFF", lineWidth: 3 },
          },
        },
      };
    });

    Highcharts.chart("scatter-jitter-container", {
      chart: {
        type: "scatter",
        zoomType: "xy",
        height: 700,
        spacing: [40, 20, 40, 20],
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        style: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        },
      },
      credits: { enabled: false },
      navigation: {
        buttonOptions: { enabled: false },
      },
      legend: { enabled: false },
      title: {
        text: "Medicare Percentage Analysis",
        style: {
          color: "#1E293B",
          fontSize: "24px",
          fontWeight: "700",
          fontFamily: "'Inter', sans-serif",
        },
        align: "left",
        margin: 40,
      },
      subtitle: {
        text: `Showing ${range[0].toLocaleString()}% – ${range[1].toLocaleString()}% range`,
        style: {
          color: "#64748B",
          fontSize: "14px",
          fontWeight: "400",
        },
        align: "left",
      },
      xAxis: {
        categories,
        title: {
          text: "Billing Codes",
          style: {
            fontSize: "15px",
            fontWeight: "600",
            color: "#475569",
          },
        },
        labels: {
          style: {
            color: "#64748B",
            fontSize: "12px",
            fontWeight: "500",
          },
        },
        lineColor: "#E2E8F0",
        lineWidth: 1,
      },
      yAxis: yAxisConfig,
      plotOptions: {
        scatter: {
          jitter: variationConfig.jitter,
          cursor: "pointer",
          states: {
            hover: {
              halo: {
                size: 8,
                attributes: {
                  fill: "rgba(62, 62, 140, 0.1)",
                  stroke: "#3e3e8c",
                  "stroke-width": 2,
                },
              },
            },
          },
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: "white",
        borderColor: "#A9A9A9",
        borderRadius: 7,
        style: { fontFamily: "'Roboto Flex'" },
        headerFormat:
          `<span style="font-size: 14px; font-weight: 600">{series.name}</span><br/>` +
          `<span style="color: #E96926;">─────</span><br/>`,
        pointFormatter: function () {
          return `<div>
                    <div style="color: #64748B; font-size: 11px;">MEDICARE %</div>
                    <div style="font-size: 16px; font-weight: 700;">
                      ${Highcharts.numberFormat(this.y, 2, ".", ",")}%
                    </div>
                  </div>
                  <div style="margin-top: 10px; color: #64748B; font-size: 11px;">NEGOTIATED RATE</div>
                  <div style="font-size: 15px; font-weight: 600; color: #1E293B;">
                    $${Highcharts.numberFormat(this.negotiated, 2, ".", ",")}
                  </div>
                  <div style="margin-top: 10px; color: #64748B; font-size: 11px;">PROVIDER</div>
                  <div style="font-size: 14px; font-weight: 600; color: #334155;">
                    ${this.provider}
                  </div>`;
        },
      },
      series,
    });
  }, [range]);

  const applyFilter = useCallback(() => {
    setIsApplying(true);

    let [min, max] = tempInputRange.map((v) => Number(v) || 0);

    // Validate and adjust values
    if (min < 0) min = 0;
    if (max > MAX_DATA_VALUE) max = MAX_DATA_VALUE;
    if (min > max) min = max;

    // Update all states
    setRange([min, max]);
    setInputRange([min, max]);

    setTimeout(() => setIsApplying(false), 300);
  }, [tempInputRange]);

  /** --------------------------------
   * Update temp input range on change
   * -------------------------------- */
  const handleInputChange = (index, value) => {
    const numValue = Number(value) || 0;
    const newTempRange = [...tempInputRange];
    newTempRange[index] = numValue;
    setTempInputRange(newTempRange);

    // Auto-validate and apply if both inputs are valid
    if (
      index === 1 &&
      newTempRange[0] <= numValue &&
      numValue <= MAX_DATA_VALUE
    ) {
      setInputRange(newTempRange);
    }
  };

  /** --------------------------------
   * Handle slider change
   * -------------------------------- */
  const handleSliderChange = (val) => {
    setRange(val);
    setInputRange(val);
    setTempInputRange(val);
  };

  /** --------------------------------
   * Reset to defaults
   * -------------------------------- */
  const resetFilters = () => {
    setRange([0, MAX_DATA_VALUE]);
    setInputRange([0, MAX_DATA_VALUE]);
    setTempInputRange([0, MAX_DATA_VALUE]);
  };

  return (
    <Container fluid className="px-4 py-3">
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
            <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
              <FiFilter size={18} className="text-primary" />
              <Card.Title className="mb-0 fs-6 text-dark">
                Filter Medicare %
              </Card.Title>
              <Badge bg="light" text="dark" className="ms-2">
                {range[0].toLocaleString()}% - {range[1].toLocaleString()}%
              </Badge>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={resetFilters}
              className="d-flex align-items-center gap-1"
            >
              <FiRefreshCw size={12} />
              Reset
            </Button>
          </div>
          {/* Slider Section */}
          <div className="mt-3">
            <Slider
              range
              min={0}
              max={MAX_DATA_VALUE}
              value={range}
              onChange={handleSliderChange}
              railStyle={{
                backgroundColor: "#E2E8F0",
                height: 4,
                borderRadius: 2,
              }}
              handleStyle={[
                {
                  borderColor: "#3e3e8c",
                  backgroundColor: "white",
                  height: 15,
                  width: 15,
                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                  marginTop: -7,
                },
                {
                  borderColor: "#3e3e8c",
                  backgroundColor: "white",
                  height: 15,
                  width: 15,
                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                  marginTop: -7,
                },
              ]}
              trackStyle={[
                {
                  backgroundColor: "#3e3e8c",
                  height: 4,
                  borderRadius: 2,
                },
              ]}
            />
          </div>

          {/* Input Controls */}
          <div className="d-flex flex-column  gap-3">
            <div className="flex-grow-1">
              <div className="d-flex flex-column flex-sm-row gap-3">
                {/* Min Input */}
                <div className="flex-grow-1">
                  <Form.Label className="small text-muted mb-1">
                    Min Value
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-white border-end-0 text-muted">
                      %
                    </InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={tempInputRange[0]}
                      onChange={(e) => handleInputChange(0, e.target.value)}
                      min={0}
                      max={MAX_DATA_VALUE}
                      className="border-start-0"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          applyFilter();
                        }
                      }}
                    />
                  </InputGroup>
                </div>

                {/* Separator */}
                <div className="text-center d-none d-sm-block">
                  <div className="text-muted fs-5">–</div>
                </div>

                {/* Max Input */}
                <div className="flex-grow-1">
                  <Form.Label className="small text-muted mb-1">
                    Max Value
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-white border-end-0 text-muted">
                      %
                    </InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={tempInputRange[1]}
                      onChange={(e) => handleInputChange(1, e.target.value)}
                      min={0}
                      max={MAX_DATA_VALUE}
                      className="border-start-0"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          applyFilter();
                        }
                      }}
                    />
                  </InputGroup>
                </div>
              </div>
            </div>
            <div className="d-flex gap-3"></div>
          </div>
        </Card.Body>
      </Card>

      <Alert
        variant="info"
        className="d-flex align-items-center py-2 px-3 mb-3 border-0 shadow-sm"
        style={{
          fontSize: "0.85rem",
          backgroundColor: "#eef2ff",
          color: "#4338ca",
        }}
      >
        <FiInfo className="me-2" size={16} />
        <div>
          <strong>Note:</strong> To maintain optimal performance, some data
          points may be sampled or hidden.
          {/* <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Large datasets are limited in the view to ensure smooth
                interaction. Use the filters above to narrow your search.
              </Tooltip>
            }
          >
            <span
              className="ms-1 text-decoration-underline"
              style={{ cursor: "pointer" }}
            >
              Why am I seeing this?
            </span>
          </OverlayTrigger> */}
        </div>
      </Alert>
      {/* Chart Container */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div
            id="scatter-jitter-container"
            className="rounded-3 overflow-hidden"
            style={{ height: "700px" }}
          />
        </Card.Body>
      </Card>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
        .border-start-0 {
          border-left: 0 !important;
        }
        .border-end-0 {
          border-right: 0 !important;
        }
        .rounded-3 {
          border-radius: 12px !important;
        }
      `}</style>
    </Container>
  );
};

export default ScatterJitterPlot;
